import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';

/** Eén interactieregel in het hulp-popovertje. */
export interface HdTimelineHelpEntry {
  /** Kale FA-naam. */
  readonly icon: string;
  readonly textKey: string;
}

/**
 * Klein herbruikbaar hulp-popovertje voor tijdlijn-achtige componenten
 * (spec `docs/LEVENSTIJDLIJN.md` §2.4): een vraagteken-knop die de
 * interactieregels (slepen, shift+scroll, ctrl+scroll, klikken) opsomt.
 * De host levert de regels; buiten klikken sluit het popovertje.
 */
@Component({
  selector: 'hd-timeline-help',
  standalone: true,
  imports: [TranslocoModule],
  template: `
    <button
      type="button"
      class="hd-timeline-help__button"
      [class.is-open]="open()"
      [attr.aria-expanded]="open()"
      [title]="'humanDesign.timelineHelp.button' | transloco"
      (click)="toggle()"
      data-test-id="timeline-help-button"
    >
      <i class="fas fa-circle-question" aria-hidden="true"></i>
    </button>
    @if (open()) {
      <div class="hd-timeline-help__popover" data-test-id="timeline-help-popover">
        @for (entry of entries(); track entry.textKey) {
          <div class="hd-timeline-help__row">
            <i class="fas fa-{{ entry.icon }}" aria-hidden="true"></i>
            <span>{{ entry.textKey | transloco }}</span>
          </div>
        }
      </div>
    }
  `,
  styles: `
    :host {
      position: relative;
      display: inline-flex;
    }

    .hd-timeline-help__button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      padding: 0;
      border: 1px solid var(--separator);
      border-radius: 50%;
      background: var(--content-surface);
      color: var(--primary-75);
      font: inherit;
      font-size: 0.8rem;
      cursor: pointer;

      &:hover,
      &.is-open {
        background: var(--highlight);
        color: var(--dark-base);
      }
    }

    .hd-timeline-help__popover {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      z-index: 10;
      min-width: 260px;
      padding: 10px 12px;
      border: 1px solid var(--separator);
      border-radius: var(--radius-lg);
      background: var(--content-surface);
      box-shadow: var(--shadow-md, 0 4px 14px rgba(0, 0, 0, 0.15));
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .hd-timeline-help__row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.78rem;
      color: var(--dark-base);

      i {
        flex: 0 0 16px;
        text-align: center;
        color: var(--primary-75);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTimelineHelpComponent {
  readonly entries = input.required<readonly HdTimelineHelpEntry[]>();

  protected readonly open = signal(false);

  private readonly host = inject(ElementRef<HTMLElement>);

  constructor() {
    // Buiten klikken sluit het popovertje (capture: vóór stopPropagation van
    // andere componenten; zelfde patroon als hd-birth-date-input).
    effect(onCleanup => {
      if (!this.open()) return;
      const listener = (event: Event): void => {
        if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
      };
      document.addEventListener('pointerdown', listener, true);
      onCleanup(() => document.removeEventListener('pointerdown', listener, true));
    });
  }

  protected toggle(): void {
    this.open.update(open => !open);
  }
}
